// Void surfaces (§12.5): the plane at the kill height (y = −3) that every fall ends in, with a
// map-specific shader. Amber tea swirls round a vortex with toon swirl strokes, twinkles and a
// crema ring outside the saucer's footprint; milk rolls in creamy swells with soft caustic threads
// and a milk-crown round the cake stand; soap suds are toon-shaded bubbles over aqua water; the
// Lobby floats on a lilac cloud sea; Pinball's neon grid scrolls 40 m below the table. Liquids
// reflect the map's sky. The arena casts a soft contact shadow on the surface (a rounded-box SDF
// offset along the key light), which is what sells the diorama as floating. KO splashes ripple
// the surface itself (8 ripple slots).
import { Color, Mesh, PlaneGeometry, ShaderMaterial, UniformsLib, UniformsUtils, Vector2, Vector3, Vector4, type ColorRepresentation } from 'three';
import { TUNE } from '@shared/constants';
import type { VoidKind } from '@shared/maps/mapTypes';
import type { QualityTier } from '../../Renderer';
import { GLSL_NOISE } from '../kit/glsl';
import type { EnvUniforms } from '../kit/materials';

export const VOID_Y = TUNE.edge.killZ;
/** Pinball's grid is drawn far below the splash height (§12.5). */
export const GRID_DEPTH = 40;
const VOID_SIZE = 420;
const RIPPLE_SLOTS = 8;
const RIPPLE_LIFE_S = 3.2;

const QUALITY_LEVEL: Readonly<Record<QualityTier, number>> = { potato: 0, low: 1, medium: 2, high: 3 };

export interface VoidColors {
  a: ColorRepresentation;   // main body (tea, milk, foam, cloud, grid base)
  b: ColorRepresentation;   // secondary (foam, caustics, suds, cloud tops, grid lines)
  c: ColorRepresentation;   // tertiary accent (reflection / highlight tint)
}

/** The arena footprint that shadows the surface: a rounded box in sim coordinates. */
export interface VoidShadow { x: number; y: number; hx: number; hy: number; r: number; strength: number }

const VERTEX = /* glsl */ `
varying vec3 vWorld;
#include <common>
#include <fog_pars_vertex>
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorld = wp.xyz;
  vec4 mvPosition = viewMatrix * wp;
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}`;

const COMMON = /* glsl */ `
uniform float uTime;
uniform vec3 uA;
uniform vec3 uB;
uniform vec3 uC;
uniform vec4 uRip[${RIPPLE_SLOTS}];
uniform vec2 uCenter;
uniform float uEdgeR;
uniform vec3 uSunDir;
uniform vec4 uShadow;      // center (x, z), half extents (x, z) in three.js coordinates
uniform vec2 uShadowR;     // corner radius, strength
uniform vec3 uPour;        // a foam spot (tea pour): x, z, radius (0 = none)
uniform vec3 uSkyTop;
uniform vec3 uSkyBottom;
varying vec3 vWorld;
#include <common>
#include <fog_pars_fragment>
${GLSL_NOISE}
// Ring waves from KO splashes: x, y = center (three.js x, z), z = start time, w = strength.
float ripples(vec2 p, out float crest) {
  float h = 0.0;
  crest = 0.0;
  for (int i = 0; i < ${RIPPLE_SLOTS}; i++) {
    vec4 r = uRip[i];
    float age = uTime - r.z;
    if (r.w <= 0.0 || age < 0.0 || age > ${RIPPLE_LIFE_S.toFixed(1)}) continue;
    float d = length(p - r.xy);
    float fade = (1.0 - age / ${RIPPLE_LIFE_S.toFixed(1)});
    fade *= fade;
    for (int k = 0; k < 3; k++) {
      float front = age * (3.0 + float(k) * 1.4) + 0.25;
      float w = d - front;
      h += r.w * exp(-w * w * 6.0) * fade * (1.0 - float(k) * 0.3);
    }
    crest += r.w * exp(-d * d * 1.2) * smoothstep(0.7, 0.0, age);
  }
  return h;
}
// Soft contact shadow of the arena footprint (0 outside, 1 deep inside).
float arenaShadow(vec2 p) {
  vec2 q = abs(p - uShadow.xy) - uShadow.zw;
  float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - uShadowR.x;
  return (1.0 - smoothstep(-1.2, 2.6, d)) * uShadowR.y;
}
// World gradient of a scalar field h(p) from its screen-space derivatives (one sample instead of
// two finite-difference lookups): solves dh = g · dp for the two screen axes.
vec2 envGradient(float h, vec2 p) {
  vec2 px = dFdx(p);
  vec2 py = dFdy(p);
  float hx = dFdx(h);
  float hy = dFdy(h);
  float det = px.x * py.y - px.y * py.x;
  if (abs(det) < 1e-9) return vec2(0.0);
  return vec2(hx * py.y - hy * px.y, px.x * hy - py.x * hx) / det;
}
float fresnelUp(vec3 world) {
  vec3 v = normalize(cameraPosition - world);
  return pow(1.0 - clamp(v.y, 0.0, 1.0), 3.0);
}
`;

const FRAG_END = /* glsl */ `
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

const BODIES: Readonly<Record<VoidKind, string>> = {
  tea: /* glsl */ `
float teaH(vec2 q) {
  return envFbm(q * 0.5 + vec2(uTime * 0.07, -uTime * 0.05), QUALITY >= 2 ? 3 : 2);
}
void main() {
  vec2 p = vWorld.xz - uCenter;
  float r = length(p);
  float a = atan(p.y, p.x);
  // Swirl coordinates: the tea turns slowly, twisting tighter under the saucer.
  float tw = a + uTime * 0.035 + 7.0 / (r + 5.0);
  vec2 q = vec2(cos(tw), sin(tw)) * r;
  float n = envFbm(q * 0.07 + vec2(uTime * 0.01, 3.0), QUALITY >= 2 ? 4 : 2);
  // Amber body: deep in the troughs, glowing where the light passes through thin tea.
  vec3 deep = uA * vec3(0.5, 0.36, 0.3);
  vec3 amber = uA * vec3(2.2, 2.3, 1.5);
  vec3 col = mix(deep, uA * 1.08, smoothstep(0.16, 0.56, n));
  col = mix(col, amber, smoothstep(0.55, 0.9, n) * 0.5);
  // Stirred-milk ribbons trailing round the vortex. (Potato breaks them up with the body noise
  // instead of two more noise lookups.)
  #if QUALITY >= 1
    float breakA = envNoise(q * 0.08 + 4.0);
    float breakB = envNoise(q * 0.11 + vec2(9.0, uTime * 0.03));
  #else
    float breakA = fract(n * 3.7);
    float breakB = fract(n * 5.3 + 0.4);
  #endif
  float rib = sin(tw * 3.0 + r * 0.3 + n * 5.0);
  float ribbon = smoothstep(0.8, 0.99, rib) * smoothstep(0.4, 0.66, breakA) * smoothstep(uEdgeR + 34.0, uEdgeR + 3.0, r);
  col = mix(col, mix(uA * 1.3, uB, 0.42), ribbon * 0.32);
  // Toon swirl lines: thin creamy strokes curling round the vortex, breaking up with the noise.
  float sl = abs(sin(tw * 2.0 + r * 0.42 + n * 3.2));
  float swirlLine = (1.0 - smoothstep(0.0, 0.05 + 0.03 * smoothstep(10.0, 30.0, r), sl)) * smoothstep(0.45, 0.62, breakB);
  swirlLine *= smoothstep(uEdgeR + 30.0, uEdgeR + 2.0, r) * smoothstep(uEdgeR - 1.0, uEdgeR + 1.5, r);
  col = mix(col, mix(uA * 1.6, uB, 0.55), swirlLine * 0.55);
  // Wavelets: the normal from a height field drives sky reflections and sun glints. Potato (software
  // GL) keeps the flat surface: six noise lookups per pixel for a detail that reads at 1-2 px.
  #if QUALITY >= 1
    float h = teaH(q);
    float e = 0.25;
    vec3 nrm = normalize(vec3((h - teaH(q + vec2(e, 0.0))) * 0.9, 1.0, (h - teaH(q + vec2(0.0, e))) * 0.9));
  #else
    vec3 nrm = vec3(0.0, 1.0, 0.0);
  #endif
  vec3 v = normalize(cameraPosition - vWorld);
  vec3 rfl = reflect(-v, nrm);
  float sun = max(dot(rfl, normalize(uSunDir)), 0.0);
  // Crema: one continuous creamy ring outside the saucer's footprint (so a fallen rim wedge opens
  // onto dark tea), wobbling gently, and round foam beads drifting further out.
  #if QUALITY >= 1
    float wob = (envNoise(vec2(a * 3.0, uTime * 0.12)) - 0.5) * 0.9 + (envNoise(vec2(a * 9.0, 4.0)) - 0.5) * 0.35;
    float hw = 0.3 + 0.14 * envNoise(vec2(a * 6.0, 1.3));
  #else
    // Potato: one angular noise drives both the ring's wobble and its width.
    float wn0 = envNoise(vec2(a * 3.0, uTime * 0.12));
    float wob = (wn0 - 0.5) * 1.1;
    float hw = 0.3 + 0.14 * fract(wn0 * 4.1);
  #endif
  float rr = uEdgeR + 1.9 + wob;
  float ring = 1.0 - smoothstep(hw - 0.12, hw + 0.04, abs(r - rr));
  vec2 bgp = q * 0.42 + vec2(uTime * 0.05, 0.0);
  vec2 bcell = floor(bgp);
  float bh = envHash12(bcell);
  vec2 bo = fract(bgp) - 0.5 - (envHash22(bcell) - 0.5) * 0.4;
  float beadR = 0.09 + 0.11 * fract(bh * 7.7);
  float isl = (1.0 - smoothstep(beadR - 0.03, beadR + 0.01, length(bo))) * step(0.74, bh)
    * smoothstep(uEdgeR + 18.0, uEdgeR + 6.0, r) * smoothstep(uEdgeR + 2.8, uEdgeR + 3.6, r);
  float foam = max(ring, isl);
  // The teapot's pour: a churning foam patch with rings.
  if (uPour.z > 0.0) {
    float dp = length(vWorld.xz - uPour.xy);
    float churn = envNoise(vec2(atan(vWorld.z - uPour.y, vWorld.x - uPour.x) * 4.0, dp * 3.0 - uTime * 3.0));
    foam = max(foam, (1.0 - smoothstep(uPour.z * 0.45, uPour.z, dp + churn * 0.45)));
    float ringW = abs(fract(dp * 0.8 - uTime * 0.7) - 0.5);
    foam = max(foam, (1.0 - smoothstep(0.0, 0.07, ringW)) * smoothstep(uPour.z * 4.0, uPour.z * 1.1, dp) * 0.45);
  }
  foam = clamp(foam, 0.0, 1.0);
  // Foam color: cream with micro-bubbles (light domes, soft rims, a glint each), never holes.
  vec3 foamCol = uB;
  #if QUALITY >= 1
  if (foam > 0.01) {
    vec4 c1 = envCells(q * 2.6 + vec2(uTime * 0.12, -uTime * 0.05));
    float br = 0.26 + 0.18 * c1.w;
    float d1 = c1.z / br;
    float dome = 1.0 - smoothstep(0.55, 1.0, d1);
    float bRim = smoothstep(0.78, 0.98, d1) * (1.0 - smoothstep(0.98, 1.2, d1)) * step(0.35, c1.w);
    foamCol = uB * (0.95 + 0.06 * dome) - vec3(0.03, 0.04, 0.045) * bRim;
    foamCol += vec3(1.0, 0.97, 0.9) * (1.0 - smoothstep(0.0, 0.3, length(c1.xy / br - vec2(-0.32, -0.3)))) * 0.08 * step(0.35, c1.w);
  }
  #endif
  // Volume: the foam's edge lifts (brighter crest) and shades the tea right outside it.
  float crest = smoothstep(0.35, 0.6, foam) * (1.0 - smoothstep(0.6, 0.95, foam));
  foamCol *= 0.94 + 0.1 * crest;
  float lip = smoothstep(0.0, 0.3, foam) * (1.0 - smoothstep(0.3, 0.55, foam));
  col *= 1.0 - 0.28 * lip;
  float rc;
  float rip = ripples(vWorld.xz, rc);
  float sh = arenaShadow(vWorld.xz);
  col *= 1.0 - 0.42 * sh;
  // Sky reflection on the liquid (stylized fresnel), strongest toward the horizon.
  float fres = 0.03 + 0.45 * pow(1.0 - clamp(dot(nrm, v), 0.0, 1.0), 4.0);
  vec3 skyCol = mix(uSkyBottom, uSkyTop, smoothstep(0.0, 0.7, rfl.y)) * vec3(1.0, 0.78, 0.55);
  col = mix(col, skyCol * 0.8, fres * (1.0 - sh * 0.7));
  float fm = smoothstep(0.42, 0.62, foam);
  col = mix(col, foamCol * (1.0 - 0.18 * sh), fm);
  col = mix(col, uB * 1.05, clamp(rip * 0.7 + rc, 0.0, 1.0));
  // Sun glints on the wavelets (not under the arena's shadow).
  #if QUALITY >= 1
    float glint = smoothstep(0.986, 0.998, sun) * (0.55 + 0.45 * envNoise(vWorld.xz * 6.0 + uTime * 2.0));
    col += glint * 1.2 * vec3(1.0, 0.96, 0.86) * (1.0 - fm) * (1.0 - sh);
  #endif
  col += pow(sun, 28.0) * 0.16 * vec3(1.0, 0.9, 0.75) * (1.0 - fm);
  // Twinkles on the wavelet crests: little stars that blink in and out.
  #if QUALITY >= 1
    vec2 sg = vWorld.xz * 1.25;
    vec2 sc = floor(sg);
    float sr = envHash12(sc);
    vec2 so = fract(sg) - 0.5 - (envHash22(sc) - 0.5) * 0.6;
    float life = fract(uTime * (0.3 + sr * 0.5) + sr * 13.0);
    float blink = smoothstep(0.0, 0.12, life) * (1.0 - smoothstep(0.12, 0.42, life));
    float star = max(1.0 - smoothstep(0.0, 0.07, length(so)), (1.0 - smoothstep(0.0, 0.025, min(abs(so.x), abs(so.y)))) * (1.0 - smoothstep(0.0, 0.2, length(so))) * 0.8);
    col += star * blink * step(0.84, sr) * smoothstep(0.42, 0.7, h) * (1.0 - fm) * (1.0 - sh) * vec3(1.0, 0.93, 0.78) * 1.3;
  #endif
${FRAG_END}`,

  milk: /* glsl */ `
float swell(vec2 p) {
  return envFbm(p * 0.085 + vec2(uTime * 0.02, uTime * 0.013), QUALITY >= 2 ? 4 : 2);
}
void main() {
  vec2 p = vWorld.xz;
  float h = swell(p);
  float e = 0.6;
  #if QUALITY >= 1
    vec3 nrm = normalize(vec3((h - swell(p + vec2(e, 0.0))) * 2.2, 0.12, (h - swell(p + vec2(0.0, e))) * 2.2));
  #else
    // Potato: screen-space derivatives of the one swell sample stand in for two more fbm lookups.
    vec2 g = envGradient(h, p);
    vec3 nrm = normalize(vec3(-g.x * e * 2.2, 0.12, -g.y * e * 2.2));
  #endif
  vec3 sunD = normalize(uSunDir);
  float ndl = dot(nrm, sunD);
  // Creamy body with a cool powder-blue shade in the troughs (a clear two-tone, like the toon ramp:
  // the live review read the old near-white milk as a blown-out backdrop behind the pink cake).
  vec3 cream = uA * vec3(1.0, 0.985, 0.95);
  vec3 cool = uB * vec3(0.8, 0.85, 0.97);
  float lit = smoothstep(0.08, 0.5, ndl);
  vec3 col = mix(cool, cream, lit);
  // Caustic shimmer: soft light threads that drift with the swell.
  float caust = 0.0;
  vec2 q = p * 0.3 + vec2(uTime * 0.05, uTime * 0.03) + nrm.xz * 0.9;
  #if QUALITY >= 2
    vec2 v1 = envVoronoi(q);
    vec2 v2 = envVoronoi(q * 1.6 - vec2(uTime * 0.06, -uTime * 0.04) + 3.1);
    caust = smoothstep(0.1, 0.0, v1.y) * 0.65 + smoothstep(0.07, 0.0, v2.y) * 0.4;
  #elif QUALITY >= 1
    vec2 v1 = envVoronoi(q);
    caust = smoothstep(0.11, 0.0, v1.y) * 0.8;
  #else
    caust = smoothstep(0.62, 0.9, envNoise(q * 2.2)) * 0.5;
  #endif
  caust = clamp(caust, 0.0, 1.0);
  col = mix(col, uB * 0.9, caust * 0.45 * (1.0 - lit));
  col += vec3(1.0, 1.0, 1.0) * caust * 0.08 * lit;
  // Glossy milk: a broad sheen and a crisp toon highlight on the swells.
  vec3 v = normalize(cameraPosition - vWorld);
  vec3 hv = normalize(sunD + v);
  float spec = max(dot(nrm, hv), 0.0);
  col += vec3(1.0, 0.99, 0.96) * (pow(spec, 12.0) * 0.06 + smoothstep(0.965, 0.975, spec) * 0.12);
  // A milk crown of slow rings around the cake stand's foot.
  if (uPour.z > 0.0) {
    float dp = length(p - uPour.xy);
    float wave = abs(fract(dp * 0.55 - uTime * 0.35) - 0.5);
    float rings = (1.0 - smoothstep(0.0, 0.06, wave)) * smoothstep(uPour.z * 5.0, uPour.z * 1.05, dp) * step(uPour.z, dp);
    col = mix(col, vec3(1.0), rings * 0.35);
    col = mix(col, cool * 0.92, (1.0 - smoothstep(uPour.z, uPour.z + 0.5, dp)) * step(uPour.z * 0.85, dp) * 0.6);
  }
  float rc;
  float rip = ripples(p, rc);
  float sh = arenaShadow(p);
  col = mix(col, cool * vec3(0.84, 0.82, 0.95), 0.55 * sh);
  col = mix(col, vec3(1.0), clamp(rip * 0.45 + rc * 0.6, 0.0, 1.0));
  col -= vec3(0.05, 0.04, 0.0) * clamp(rip, 0.0, 1.0);
  float fres = pow(1.0 - clamp(dot(nrm, v), 0.0, 1.0), 4.0);
  vec3 rflv = reflect(-v, nrm);
  col = mix(col, mix(uSkyBottom, uSkyTop, smoothstep(0.0, 0.7, rflv.y)), fres * 0.4);
${FRAG_END}`,

  foam: /* glsl */ `
// One layer of soap bubbles: domes of per-cell radius, some cells empty. Returns coverage; n is
// the dome normal (three.js xz from the cell offset), rim the soft edge band.
float sudsLayer(vec2 p, float fill, out vec3 n, out float rim, out float id) {
  #if QUALITY >= 1
    vec4 c = envCells(p);
    float rad = mix(0.38, 0.66, fract(c.w * 3.7));
  #else
    // Potato (software GL): one bubble per cell, near its center and never past its edge, instead
    // of the 3×3 nearest-feature search (nine hashes per pixel); the swirl hides the lattice.
    // Odd rows are staggered half a cell (a packed, hex-like arrangement reads less like a grid).
    vec2 sp = vec2(p.x + 0.5 * mod(floor(p.y), 2.0), p.y);
    vec2 cell = floor(sp);
    float cid = envHash12(cell);
    vec2 off = fract(sp) - 0.5 - (vec2(cid, fract(cid * 7.13)) - 0.5) * 0.2;
    // d = dist / rad < 0.9 keeps each dome inside its cell (at most 0.4 from the jittered center).
    vec4 c = vec4(off, length(off), cid);
    float rad = mix(0.24, 0.44, fract(cid * 3.7));
  #endif
  float on = step(1.0 - fill, c.w);
  float d = c.z / rad;
  float cover = (1.0 - smoothstep(0.9, 1.0, d)) * on;
  float hgt = sqrt(max(0.0, 1.0 - min(d, 1.0) * min(d, 1.0)));
  n = normalize(vec3(c.x / rad, hgt + 0.35, c.y / rad));
  rim = smoothstep(0.72, 0.97, d) * cover;
  id = c.w;
  return cover;
}
void main() {
  vec2 c = vWorld.xz - uCenter;
  float r = length(c);
  // The suds churn around the drum.
  float ang = atan(c.y, c.x) + uTime * 0.06 + 4.0 / (r + 6.0);
  vec2 w = vec2(cos(ang), sin(ang)) * r;
  vec3 sunD = normalize(uSunDir);
  vec3 v = normalize(cameraPosition - vWorld);
  vec3 n1; vec3 n2; float rim1; float rim2; float id1; float id2;
  // Bubbles gather in drifting rafts with open soapy water between them, so the white drum reads
  // against a mid-value surface (a solid carpet of suds washed the whole frame out).
  float raft = smoothstep(0.4, 0.6, envNoise(w * 0.05 + vec2(uTime * 0.015, -uTime * 0.01)));
  float big = sudsLayer(w * 0.42, 0.78 * raft, n1, rim1, id1);
  float cover = big;
  vec3 n = n1;
  float rim = rim1;
  float id = id1;
  #if QUALITY >= 1
    float small = sudsLayer(w * 1.25 + 11.0, mix(0.12, 0.8, raft), n2, rim2, id2);
    float top = step(0.5, big);
    n = mix(n2, n1, top);
    rim = mix(rim2, rim1, top);
    id = mix(id2, id1, top);
    cover = max(big, small);
  #endif
  // Toon-shaded bubbles: three soft steps from cool suds-blue to white, a glint, rainbow rims.
  float ndl = dot(n, sunD);
  float tone = 0.62 + 0.2 * smoothstep(0.0, 0.12, ndl) + 0.18 * smoothstep(0.45, 0.55, ndl);
  vec3 bub = mix(uB * 0.8, uA, tone);
  // A darker soap-film rim keeps each bubble a whole round shape against the water.
  bub = mix(bub, uB * vec3(0.42, 0.62, 0.78), rim * 0.7);
  vec3 hv = normalize(sunD + v);
  bub += smoothstep(0.955, 0.975, dot(n, hv)) * 0.35;
  bub += rim * 0.32 * (0.5 + 0.5 * cos(6.2831 * (id * 2.0 + rim * 0.8 + vec3(0.0, 0.33, 0.67) + uTime * 0.05)));
  // Soapy water between the rafts: a saturated aqua-blue (clearly darker than the white enamel
  // drum), soft swirl streaks and a sky sheen toward the horizon.
  float wn = envNoise(w * 0.3 + uTime * 0.05);
  vec3 water = mix(uB * vec3(0.2, 0.46, 0.66), uB * vec3(0.3, 0.6, 0.78), wn);
  float streak = smoothstep(0.6, 0.95, sin(ang * 6.0 + r * 0.35 + wn * 4.0));
  water = mix(water, uB * vec3(0.55, 0.8, 0.92), streak * 0.35);
  water = mix(water, uC, pow(1.0 - clamp(v.y, 0.0, 1.0), 3.0) * 0.35);
  vec3 col = mix(water, bub, cover);
  // Contact shading: bubbles shade the water right next to them.
  col *= 1.0 - 0.12 * (1.0 - cover) * smoothstep(0.0, 1.0, big + 0.3);
  float rc;
  float rip = ripples(vWorld.xz, rc);
  float sh = arenaShadow(vWorld.xz);
  col *= 1.0 - 0.3 * sh;
  col = mix(col, vec3(1.0), clamp(rip * 0.6 + rc, 0.0, 1.0));
  col = mix(col, uC, fresnelUp(vWorld) * 0.3);
${FRAG_END}`,

  lilac: /* glsl */ `
float cloudH(vec2 p) {
  return envFbm(p, QUALITY >= 2 ? 5 : 3);
}
void main() {
  vec2 p = vWorld.xz * 0.075 + vec2(uTime * 0.01, uTime * 0.005);
  float h = cloudH(p);
  float e = 0.03;
  #if QUALITY >= 1
    vec3 nrm = normalize(vec3((h - cloudH(p + vec2(e, 0.0))) * 2.6, 0.05, (h - cloudH(p + vec2(0.0, e))) * 2.6));
  #else
    // Potato: screen-space derivatives instead of two more fbm lookups per pixel.
    vec2 g = envGradient(h, p);
    vec3 nrm = normalize(vec3(-g.x * e * 2.6, 0.05, -g.y * e * 2.6));
  #endif
  float diff = clamp(dot(nrm, normalize(uSunDir)) * 0.6 + 0.5, 0.0, 1.0);
  float puff = smoothstep(0.3, 0.74, h);
  vec3 shade = uA * 0.8;
  vec3 lit = mix(uA, uB, 0.78);
  vec3 col = mix(shade, lit, puff * diff);
  col = mix(col, uB, smoothstep(0.64, 0.9, h) * diff * 0.65);
  float rc;
  float rip = ripples(vWorld.xz, rc);
  float sh = arenaShadow(vWorld.xz);
  col *= 1.0 - 0.28 * sh;
  col = mix(col, uB, clamp(rip * 0.7 + rc * 0.8, 0.0, 1.0));
  col = mix(col, uC, fresnelUp(vWorld) * 0.3);
${FRAG_END}`,

  grid: /* glsl */ `
void main() {
  vec2 p = vWorld.xz + vec2(0.0, uTime * 3.0);
  float cell = 4.0;
  vec2 g = abs(fract(p / cell - 0.5) - 0.5) * cell;
  vec2 fw = fwidth(p) * 1.1;
  float line = 1.0 - min(smoothstep(0.0, fw.x + 0.03, g.x), smoothstep(0.0, fw.y + 0.03, g.y));
  vec2 gm = abs(fract(p / (cell * 4.0) - 0.5) - 0.5) * cell * 4.0;
  float major = 1.0 - min(smoothstep(0.0, fw.x * 1.5 + 0.08, gm.x), smoothstep(0.0, fw.y * 1.5 + 0.08, gm.y));
  float glow = exp(-min(g.x, g.y) * 2.2) * 0.18;
  // Far below the table: the grid fades out toward the edges of the view.
  float d = length(vWorld.xz - cameraPosition.xz);
  float fade = 1.0 - smoothstep(30.0, 95.0, d);
  vec3 col = uA;
  col += uB * (line * 0.55 + glow) * fade;
  col += uC * major * 0.5 * fade;
  float pulse = 0.5 + 0.5 * sin(p.y * 0.1 - uTime * 2.0);
  col += uB * 0.05 * pulse * fade;
  col *= 1.0 - 0.6 * arenaShadow(vWorld.xz);
${FRAG_END}`,
};

export interface VoidSurface {
  readonly mesh: Mesh;
  /** Starts a ring wave at sim (x, y). */
  ripple(x: number, y: number, strength: number, now: number): void;
  dispose(): void;
}

export interface VoidOptions {
  centerX: number;
  centerY: number;
  edgeRadius: number;
  /** The arena footprint's contact shadow (offset along the key light automatically). */
  shadow?: VoidShadow;
  /** A churning foam spot (sim x, y, radius): where the Teacup's teapot pours, the Cake stand's foot. */
  pour?: { x: number; y: number; r: number };
  /** Sky gradient the liquid reflects (top, bottom). */
  sky?: { top: ColorRepresentation; bottom: ColorRepresentation };
}

export function createVoidSurface(kind: VoidKind, colors: VoidColors, tier: QualityTier, env: EnvUniforms, sunDir: Vector3, opts: VoidOptions): VoidSurface {
  const rips = Array.from({ length: RIPPLE_SLOTS }, () => new Vector4(0, 0, -100, 0));
  let next = 0;
  const y = kind === 'grid' ? VOID_Y - GRID_DEPTH : VOID_Y;
  const sun = sunDir.clone().normalize();
  // The footprint at arena height projects down to the surface along the light.
  const drop = (0 - y) / Math.max(0.2, sun.y);
  const sh = opts.shadow;
  const shadow = sh
    ? new Vector4(sh.x - sun.x * drop, sh.y - sun.z * drop, sh.hx, sh.hy)
    : new Vector4(0, 0, 0, 0);
  const mat = new ShaderMaterial({
    uniforms: {
      ...UniformsUtils.clone(UniformsLib.fog),
      uTime: env.uTime, uA: { value: new Color(colors.a) }, uB: { value: new Color(colors.b) }, uC: { value: new Color(colors.c) },
      uRip: { value: rips }, uCenter: { value: new Vector2(opts.centerX, opts.centerY) }, uEdgeR: { value: opts.edgeRadius },
      uSunDir: { value: sun }, uShadow: { value: shadow }, uShadowR: { value: new Vector2(sh?.r ?? 0, sh ? sh.strength : 0) },
      uPour: { value: new Vector3(opts.pour?.x ?? 0, opts.pour?.y ?? 0, opts.pour?.r ?? 0) },
      uSkyTop: { value: new Color(opts.sky?.top ?? colors.c) }, uSkyBottom: { value: new Color(opts.sky?.bottom ?? colors.c) },
    },
    defines: { QUALITY: QUALITY_LEVEL[tier] },
    vertexShader: VERTEX,
    fragmentShader: COMMON + BODIES[kind],
    fog: true,
  });
  const geo = new PlaneGeometry(VOID_SIZE, VOID_SIZE, 1, 1);
  geo.rotateX(-Math.PI / 2);
  const mesh = new Mesh(geo, mat);
  mesh.name = `void:${kind}`;
  mesh.position.y = y;
  // Drawn after the arena, props and beans (still in the opaque pass): early depth rejection then
  // skips every void pixel the arena covers. At −5 it was drawn first and its procedural shader ran
  // under the whole arena too, the largest single cost of Potato on software GL (INT-B).
  mesh.renderOrder = 5;
  mesh.frustumCulled = false;
  return {
    mesh,
    ripple(x, yy, strength, now) {
      rips[next].set(x, yy, now, strength);
      next = (next + 1) % RIPPLE_SLOTS;
    },
    dispose() {
      geo.dispose();
      mat.dispose();
    },
  };
}
